import { Badge, Group, Paper, Stack, Text } from '@mantine/core';
import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { TravelDayBalanceSection } from '@/module/Travel/Request/components/TravelDayBalanceSection';
import { TravelRegistryTypeBadge } from '@/module/Travel/Request/components/TravelRegistryTypeBadge';
import { TravelRequestStatusBadge } from '@/module/Travel/Request/components/TravelRequestStatusBadge';
import { formatTravelRegistration } from '@/module/Travel/Request/model/TravelRequestTypes';

import { TitleCard } from '@/core/components';

import { formatDate, notEmptyArray } from '@feature/utils';

import { TravelRegistryRow } from '../model/TravelRegistryTypes';
import classes from './TravelRegistryCard.module.css';

export interface TravelRegistryCardProps {
  row: TravelRegistryRow;
}

export function TravelRegistryCard({ row }: TravelRegistryCardProps) {
  const { t } = useTranslation();
  const {
    id,
    registrationNumber,
    registrationYear,
    registrationDate,
    registryType,
    requester,
    jobTitle,
    facultyName,
    departmentName,
    disciplineName,
    activityType,
    name,
    destinationInstitution,
    city,
    country,
    departureDate,
    returnDate,
    travelDays,
    eventStartDate,
    eventEndDate,
    eventDays,
    status,
    lateSubmission,
    leaveDaysExceeded,
    dayCapExceeded,
    dayBalance,
  } = row;

  const activityTypeLabel = t(`travel.activityTypeEnum.${activityType}`);
  const destination = [destinationInstitution, city, country?.name].filter(notEmptyArray).join(', ');
  const hasEventPeriod = eventStartDate != null || eventEndDate != null;
  const affiliation = [jobTitle, facultyName, departmentName, disciplineName].filter(notEmptyArray).join(', ');

  return (
    <Paper
      className={classes.card}
      component={Link}
      to={`/travel/requests/${id}`}
      p="lg"
      c="var(--mantine-color-text)"
    >
      <Stack gap="10px">
        <TitleCard
          title={name ?? activityTypeLabel}
          pre={
            <Text fw="500">{formatTravelRegistration({ registrationNumber, registrationYear, registrationDate })}</Text>
          }
          below={<TravelRegistryTypeBadge registryType={registryType} />}
        />
        <Group>
          <Text size="sm">{requester.name}</Text>
          {affiliation !== '' && (
            <Text
              c="dimmed"
              size="sm"
            >
              {affiliation}
            </Text>
          )}
        </Group>
        <Group>
          <Text
            c="dimmed"
            size="sm"
          >
            {activityTypeLabel}
          </Text>
          <Text
            c="dimmed"
            size="sm"
          >
            {destination === '' ? '-' : destination}
          </Text>
          <Text
            c="dimmed"
            size="sm"
          >
            {formatDate(departureDate) ?? '-'} - {formatDate(returnDate) ?? '-'}
            {travelDays != null && ` · ${t('travel.registry.travelDays', { count: travelDays })}`}
          </Text>
        </Group>
        {hasEventPeriod && (
          <Text
            c="dimmed"
            size="sm"
          >
            {formatDate(eventStartDate) ?? '-'} - {formatDate(eventEndDate) ?? '-'}
            {eventDays != null && ` · ${t('travel.registry.eventDays', { count: eventDays })}`}
          </Text>
        )}
        <Group>
          <TravelRequestStatusBadge status={status} />
          {lateSubmission && <Badge color="red">{t('travel.requests.view.lateSubmission')}</Badge>}
        </Group>
        <TravelDayBalanceSection
          activityType={activityType}
          leaveDaysExceeded={leaveDaysExceeded}
          dayCapExceeded={dayCapExceeded}
          dayBalance={dayBalance}
        />
      </Stack>
    </Paper>
  );
}
